/**
 * @file interpret/engine/useInterpretSession.ts
 * @brief React binding for `InterpretController`: one stable controller per screen.
 *
 * @details
 * The controller lives in a ref, so its identity never changes between renders
 * (the phone's hooks returned new objects every render and broke effect deps).
 * State comes through `useSyncExternalStore`.
 *
 * On unmount a live session is ended and billed for the time used, so leaving
 * the page can never leave a session running on the user's card. The screen
 * also guards navigation while live (Phase 2).
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect, useRef, useSyncExternalStore } from "react";
import { createBrowserInterpretDeps } from "./browserDeps";
import {
  InterpretController,
  type InterpretDeps,
  type InterpretSnapshot,
} from "./interpretController";

export function useInterpretSession(createDeps: () => InterpretDeps = createBrowserInterpretDeps): {
  controller: InterpretController;
  snapshot: InterpretSnapshot;
} {
  const ref = useRef<InterpretController | null>(null);
  if (ref.current === null) ref.current = new InterpretController(createDeps());
  const controller = ref.current;
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);

  // Closing the tab mid-session bills the time used right away.
  useEffect(() => {
    const onPageHide = () => controller.endOnUnload();
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [controller]);

  useEffect(() => {
    return () => {
      void controller.end().finally(() => controller.dispose());
    };
  }, [controller]);

  return { controller, snapshot };
}
